import { PageInWP } from "@/lib/singularity/wp-page-manager/types";
import {
  SingularityBrand,
  NamedColorPalette,
  BorderRadius,
  ButtonSize
} from "@/types/singularity/brand";
import { AIServer } from "@/types/singularity";
import { AIJobStateForUI } from "@/futurelayer/ai-job-manager/ai-jobs";
import { TreeNode } from "@/types/tree";
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";
import { PageSection } from "@/lib/singularity/page-structure/page-structure";
import { stopInlineEditing } from "@/lib/inline-editor/inline-editor";
import { SiteState } from "@/futurelayer/settings/types";
import { SingularityFontType } from "@/lib/singularity/brand/typography";

config.rawError = true;

@Module({ name: "singularity", namespaced: true })
export default class SingularityModule extends VuexModule {
  singularityUiState: SingularityUIState = {
    singularityRightPanel: null,
    singularityLeftPanel: null,
    singularityFullWidthPanel: null
  };

  pagesInWP: PageInWP[] = [];
  headerAndFooterBlockIdsInWP: { headerId?: number; footerId?: number } = {};
  backgroundGeneratingHeadersAndFooters: TreeNode["id"][] = [];

  homepageInWP = -1;

  siteStatus: SiteStatus = {
    isClaimed: false,
    claimUrl: "",
    siteState: null
  };

  sectionAddOrSwapPanelState: SectionAddOrSwapPanelState = {
    mode: "add_after",
    sectionId: null
  };

  sectionRewritePanelState: SectionRewritePanelState = null;

  imageEditorPanelState: ImageEditorPanelState = null;

  backgroundImageEditorPanelState: BackgroundImageEditorPanelState = null;

  contentPropertiesEditorPanelState = false;

  iconEditorPanelState: IconEditorPanelState = {
    iconId: null
  };

  textEditorPanelState: TextEditorPanelState = null;

  linkEditorPanelState: LinkEditorPanelState = null;

  headerAndFooterEditorPanelState: HeaderAndFooterEditorPanelState = null;

  addNewSectionPanelState: AddNewSectionPanelState = null;

  aiJobs: AIJobStateForUI[] = [];

  aiServer: AIServer = null;

  brand: SingularityBrand = {
    color: {
      neutral: null,
      primary: null,
      secondary: []
    },
    ui: {
      inputRadius: "md",
      buttonSize: "default"
    },
    logo: {
      logo: null
    }
  };

  brandPanelState: SingularityBrandPanelState = { openedPanel: null };

  currentPalette: CurrentPaletteState = null;

  currentFont: SingularityFontType = null;

  previewMode = false;

  isSectionLibraryModalOpen = false;
  isOutOfCreditsModalOpen = false;
  isPlanExpiredModalOpen = false;

  isOpeningPageId: number | false = false;

  canvasZoom = false;

  debugSidebarPanelsEnabled = false;

  usedStockPhotoIds: string[] = [];

  @Mutation
  setCanvasZoom(active: boolean) {
    this.canvasZoom = active;
  }

  @Mutation
  setIsOpeningPageId(isOpening: number | false) {
    this.isOpeningPageId = isOpening;
  }

  @Mutation
  setSectionLibraryModalState(isOpen: boolean) {
    this.isSectionLibraryModalOpen = isOpen;
  }

  @Mutation
  setOutOfCreditsModalOpen(isOpen: boolean) {
    this.isOutOfCreditsModalOpen = isOpen;
  }

  @Mutation
  setPlanExpiredModalOpen(isOpen: boolean) {
    this.isPlanExpiredModalOpen = isOpen;
  }

  @Mutation
  setPreviewMode(previewMode: boolean) {
    this.previewMode = previewMode;
  }

  @Mutation
  setBackgroundGeneratingHeadersAndFooters(elementIds: TreeNode["id"][]) {
    this.backgroundGeneratingHeadersAndFooters = elementIds;
  }

  @Mutation
  togglePreviewMode() {
    this.previewMode = !this.previewMode;
  }

  @Mutation
  setCurrentFont(font: SingularityFontType) {
    this.currentFont = font;
  }

  @Mutation
  setHomepageInWP(pageId: number) {
    this.homepageInWP = pageId;
  }

  @Mutation
  setCurrentPalette(payload: {
    palette: NamedColorPalette;
    index: number | null;
  }) {
    this.currentPalette = {
      palette: payload.palette,
      index: payload.index
    };
  }

  @Mutation
  setBrand(brand: SingularityBrand) {
    this.brand = brand;
  }

  @Action
  setInputRadius(payload: { inputRadius: BorderRadius }) {
    const newBrand = {
      ...this.brand,
      ui: {
        ...this.brand.ui,
        inputRadius: payload.inputRadius
      }
    };
    this.setBrand(newBrand);
  }

  @Action
  setButtonSize(payload: { buttonSize: ButtonSize }) {
    const newBrand = {
      ...this.brand,
      ui: {
        ...this.brand.ui,
        buttonSize: payload.buttonSize
      }
    };
    this.setBrand(newBrand);
  }

  @Action
  setPalette(payload: {
    type: "primary" | "secondary" | "neutral";
    palette: NamedColorPalette | null;
  }) {
    const newBrand = { ...this.brand };
    switch (payload.type) {
      case "primary":
        newBrand.color = {
          ...newBrand.color,
          primary: payload.palette
        };
        break;
      case "neutral":
        newBrand.color = {
          ...newBrand.color,
          neutral: payload.palette
        };
        break;
      case "secondary":
        if (payload.palette) {
          newBrand.color = {
            ...newBrand.color,
            secondary: [...newBrand.color.secondary, payload.palette]
          };
        } else {
          newBrand.color = {
            ...newBrand.color,
            secondary: []
          };
        }
        break;
    }
    this.setBrand(newBrand);
  }

  @Action
  addSecondaryPalette(palette: NamedColorPalette) {
    const newBrand = {
      ...this.brand,
      color: {
        ...this.brand.color,
        secondary: [...this.brand.color.secondary, palette]
      }
    };
    this.setBrand(newBrand);
  }

  @Action
  removeSecondaryPalette(index: number) {
    if (index >= 0 && index < this.brand.color.secondary.length) {
      const newSecondary = [...this.brand.color.secondary];
      newSecondary.splice(index, 1);
      const newBrand = {
        ...this.brand,
        color: {
          ...this.brand.color,
          secondary: newSecondary
        }
      };
      this.setBrand(newBrand);
    }
  }

  @Action
  updateSecondaryPalette({
    index,
    palette
  }: {
    index: number;
    palette: NamedColorPalette;
  }) {
    const newSecondary = [...this.brand.color.secondary];
    newSecondary.splice(index, 1, palette);
    const newBrand = {
      ...this.brand,
      color: {
        ...this.brand.color,
        secondary: newSecondary
      }
    };
    this.setBrand(newBrand);
  }

  @Mutation
  setAiServer(aiServer: AIServer) {
    this.aiServer = aiServer;
  }

  // Actions

  @Action
  toggleCanvasZoom() {
    this.setCanvasZoom(!this.canvasZoom);
  }

  @Mutation
  setDebugSidebarPanelsEnabled(enabled: boolean) {
    this.debugSidebarPanelsEnabled = enabled;
  }

  @Action
  toggleDebugSidebarPanels() {
    this.setDebugSidebarPanelsEnabled(!this.debugSidebarPanelsEnabled);
  }

  @Action
  setSingularityRightPanel(panel: SingularityUIState["singularityRightPanel"]) {
    this.setSingularityUiState({
      ...this.singularityUiState,
      singularityRightPanel: panel,
      singularityFullWidthPanel: null
    });
  }

  @Action
  async setSingularityLeftPanel(
    panel: SingularityUIState["singularityLeftPanel"]
  ) {
    if (panel === "page_structure") {
      await stopInlineEditing();
    }

    this.setSingularityUiState({
      ...this.singularityUiState,
      singularityLeftPanel: panel,
      singularityFullWidthPanel: null
    });
  }

  @Action
  async setSingularityFullWidthPanel(
    panel: SingularityUIState["singularityFullWidthPanel"]
  ) {
    if (panel !== null) {
      this.setSingularityUiState({
        ...this.singularityUiState,
        singularityFullWidthPanel: panel,
        singularityLeftPanel: null,
        singularityRightPanel: null
      });
    }
  }

  @Mutation
  setSingularityUiState(singularityUiState: SingularityUIState) {
    this.singularityUiState = singularityUiState;
  }

  @Mutation
  setSiteStatus(siteStatus: SiteStatus) {
    this.siteStatus = siteStatus;
  }

  @Mutation
  setPagesInWP(pagesInWP: PageInWP[]) {
    this.pagesInWP = pagesInWP;
  }

  @Mutation
  setHeaderAndFooterBlockIdsInWP(headerAndFooterBlockIdsInWP: {
    headerId?: number;
    footerId?: number;
  }) {
    this.headerAndFooterBlockIdsInWP = headerAndFooterBlockIdsInWP;
  }

  @Mutation
  setAIJobs(aiJobs: AIJobStateForUI[]) {
    this.aiJobs = aiJobs;
  }

  @Mutation
  setSectionAddOrSwapPanelState(
    sectionAddOrSwapPanelState: SectionAddOrSwapPanelState
  ) {
    this.sectionAddOrSwapPanelState = sectionAddOrSwapPanelState;
  }

  @Action
  setSectionRewritePanelState(
    sectionRewritePanelState: SectionRewritePanelState
  ) {
    this.setSectionRewritePanelState_(sectionRewritePanelState);
    if (!sectionRewritePanelState) {
      this.setSingularityLeftPanel("page_structure");
    }

    this.setSingularityLeftPanel("rewrite_section");
  }

  @Action
  setImageEditorPanelState(imageEditorPanelState: ImageEditorPanelState) {
    this.setImageEditorPanelState_(imageEditorPanelState);
    if (!imageEditorPanelState) {
      this.setSingularityLeftPanel("page_structure");
    }
  }

  @Mutation
  setSectionRewritePanelState_(
    sectionRewritePanelState: SectionRewritePanelState
  ) {
    this.sectionRewritePanelState = sectionRewritePanelState;
  }

  @Mutation
  setImageEditorPanelState_(imageEditorPanelState: ImageEditorPanelState) {
    this.imageEditorPanelState = imageEditorPanelState;
  }

  @Action
  setBackgroundImageEditorPanelState(
    backgroundImageEditorPanelState: BackgroundImageEditorPanelState
  ) {
    this.setBackgroundImageEditorPanelState_(backgroundImageEditorPanelState);
    if (!backgroundImageEditorPanelState) {
      this.setSingularityLeftPanel("page_structure");
    }
  }

  @Mutation
  setBackgroundImageEditorPanelState_(
    backgroundImageEditorPanelState: BackgroundImageEditorPanelState
  ) {
    this.backgroundImageEditorPanelState = backgroundImageEditorPanelState;
  }

  @Mutation
  setContentPropertiesEditorPanelState(
    contentPropertiesEditorPanelState: boolean
  ) {
    this.contentPropertiesEditorPanelState = contentPropertiesEditorPanelState;
  }

  @Mutation
  setIconEditorPanelState(iconEditorPanelState: IconEditorPanelState) {
    this.iconEditorPanelState = iconEditorPanelState;
  }

  @Action
  setTextEditorPanelState(textEditorPanelState: TextEditorPanelState) {
    if (textEditorPanelState) {
      this.setSingularityLeftPanel("text_editor");
    } else {
      this.setSingularityLeftPanel("page_structure");
    }
    this.setTextEditorPanelState_(textEditorPanelState);
  }

  @Action
  setLinkEditorPanelState(linkEditorPanelState: LinkEditorPanelState) {
    if (linkEditorPanelState) {
      this.setSingularityLeftPanel("link_editor");
    } else {
      this.setSingularityLeftPanel("page_structure");
    }
    this.setLinkEditorPanelState_(linkEditorPanelState);
  }

  @Mutation
  setLinkEditorPanelState_(linkEditorPanelState: LinkEditorPanelState) {
    this.linkEditorPanelState = linkEditorPanelState;
  }

  @Mutation
  setTextEditorPanelState_(textEditorPanelState: TextEditorPanelState) {
    this.textEditorPanelState = textEditorPanelState;
  }

  @Action
  setHeaderAndFooterEditorPanelState(
    headerAndFooterEditorPanelState: HeaderAndFooterEditorPanelState
  ) {
    if (headerAndFooterEditorPanelState) {
      this.setSingularityLeftPanel("header_footer_editor");
    } else {
      this.setSingularityLeftPanel("page_structure");
    }
    this.setHeaderAndFooterEditorPanelState_(headerAndFooterEditorPanelState);
  }

  @Mutation
  setHeaderAndFooterEditorPanelState_(
    headerAndFooterEditorPanelState: HeaderAndFooterEditorPanelState
  ) {
    this.headerAndFooterEditorPanelState = headerAndFooterEditorPanelState;
  }

  @Mutation
  setAddNewSectionPanelState_(
    addNewSectionPanelState: AddNewSectionPanelState
  ) {
    this.addNewSectionPanelState = addNewSectionPanelState;
  }

  @Mutation
  setUsedStockPhotoIds(usedStockPhotoIds: string[]) {
    this.usedStockPhotoIds = usedStockPhotoIds;
  }

  @Action
  setAddNewSectionPanelState(addNewSectionPanelState: AddNewSectionPanelState) {
    this.setAddNewSectionPanelState_(addNewSectionPanelState);
    if (!addNewSectionPanelState) {
      this.setSingularityLeftPanel("page_structure");
    } else {
      this.setSingularityLeftPanel("add_new_section");
    }
  }

  @Action
  highlightSingularityLeftPanel() {
    this.singularityUiState.isSingularityLeftPanelHighlighted = true;
    setTimeout(() => {
      this.singularityUiState.isSingularityLeftPanelHighlighted = false;
    }, 300);
  }

  @Action
  setSingularityBrandPanelState(brandPanelState: SingularityBrandPanelState) {
    this.setSingularityLeftPanel("brand");
    this.setBrandPanelState(brandPanelState);
  }

  @Mutation
  setBrandPanelState(brandPanelState: SingularityBrandPanelState) {
    this.brandPanelState = brandPanelState;
  }
}

type SectionAddOrSwapPanelState = {
  mode: "add_before" | "add_after" | "swap";
  sectionId: null | TreeNode["id"];
};

type SectionRewritePanelState = {
  section: null | PageSection;
} | null;

export type SiteStatus = {
  isClaimed: boolean;
  claimUrl: string;
  siteState: SiteState | null;
};

export type SingularityBrandPanelState = {
  openedPanel: "color" | "logo" | "ui" | "typography" | null;
} | null;

export type SingularityUIState = {
  singularityRightPanel: null | "ai_job_manager" | "debug";
  singularityFullWidthPanel:
    | null
    | "step1"
    | "settings";
  singularityLeftPanel:
    | null
    | "page_structure"
    | "page_manager"
    | "brand"
    | "brand_edit_color"
    | "brand_edit_typography"
    | "section_add_or_swap"
    | "image_editor"
    | "background_image_editor"
    | "icon_editor"
    | "text_editor"
    | "link_editor"
    | "content_properties_editor"
    | "header_footer_editor"
    | "add_new_section"
    | "rewrite_section";
  isSingularityLeftPanelHighlighted?: boolean;
};

type AddNewSectionPanelState = {
  mode: "library" | "breakdance_ai";
} | null;

type ImageEditorPanelState = {
  elementId: TreeNode["id"];
  controlPath: string;
  selectedImageIndex?: number;
  initialStep: "main" | "options";
} | null;

type BackgroundImageEditorPanelState = {
  sectionId: TreeNode["id"];
} | null;

type IconEditorPanelState = {
  iconId: TreeNode["id"] | null;
};

type TextEditorPanelState = {
  elementId: TreeNode["id"];
  contentEditablePropertyPath: string;
} | null;

type LinkEditorPanelState = {
  elementId: TreeNode["id"];
  linkPropertyPaths: string[];
} | null;

type HeaderAndFooterEditorPanelState = {
  elementId: TreeNode["id"];
  elementType: "header" | "footer";
} | null;

type CurrentPaletteState = {
  palette: NamedColorPalette | null;
  index: null | number;
} | null;
